import React, { useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
  Fade,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { enGB } from 'date-fns/locale';
import { differenceInDays, format, addDays, isWeekend, eachDayOfInterval } from 'date-fns';

export interface LeaveRequestFormValues {
  startDate: Date | null;
  endDate: Date | null;
  reason: string;
  comment?: string;
}

export interface LeaveAllocationSummary {
  allowanceDays?: number;
  approvedDays: number;
  pendingDays: number;
  deniedDays: number;
}

interface LeaveRequestFormProps {
  onSubmit: (values: LeaveRequestFormValues) => void | Promise<void>;
  disabled?: boolean;
  submitLabel?: string;
  helperNotice?: string;
  initialValues?: LeaveRequestFormValues;
  allocationSummary?: LeaveAllocationSummary;
}

export const LeaveRequestForm: React.FC<LeaveRequestFormProps> = ({
  onSubmit,
  disabled = false,
  submitLabel = 'Submit request',
  helperNotice,
  initialValues,
  allocationSummary,
}) => {
  const defaultValues = useMemo<LeaveRequestFormValues>(
    () =>
      initialValues || {
        startDate: null,
        endDate: null,
        reason: '',
        comment: '',
      },
    [initialValues]
  );

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<LeaveRequestFormValues>({
    defaultValues,
  });

  const startDate = watch('startDate');
  const endDate = watch('endDate');

  // Calculate duration and business days
  const duration = useMemo(() => {
    if (!startDate || !endDate) return null;

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (end < start) return null;

    const totalDays = differenceInDays(end, start) + 1;

    // Calculate business days (excluding weekends)
    const allDays = eachDayOfInterval({ start, end });
    const businessDays = allDays.filter(day => !isWeekend(day)).length;
    const weekendDays = totalDays - businessDays;

    return {
      total: totalDays,
      business: businessDays,
      weekends: weekendDays,
      startFormatted: format(start, 'EEE, dd MMM yyyy'),
      endFormatted: format(end, 'EEE, dd MMM yyyy'),
    };
  }, [startDate, endDate]);

  const submitHandler = async (values: LeaveRequestFormValues) => {
    await onSubmit(values);
    reset(defaultValues);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Box component="form" onSubmit={handleSubmit(submitHandler)} noValidate>
        <Stack spacing={3}>
          {/* Helper Notice */}
          {helperNotice && (
            <Alert
              severity="info"
              variant="filled"
              icon={<EventAvailableIcon />}
              sx={{
                backgroundColor: 'info.main',
                '& .MuiAlert-message': { width: '100%' },
              }}
            >
              {helperNotice}
            </Alert>
          )}

          {/* Date Selection Card */}
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                <CalendarMonthIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Leave Period
                </Typography>
              </Box>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Controller
                  name="startDate"
                  control={control}
                  rules={{ required: 'Start date is required' }}
                  render={({ field }) => (
                    <DatePicker
                      label="Start date"
                      value={field.value}
                      onChange={field.onChange}
                      disabled={disabled || isSubmitting}
                      format="dd/MM/yyyy"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: Boolean(errors.startDate),
                          helperText: errors.startDate?.message,
                        },
                      }}
                    />
                  )}
                />

                <Controller
                  name="endDate"
                  control={control}
                  rules={{
                    required: 'End date is required',
                    validate: (value) => {
                      if (!value || !startDate) return true;
                      if (value.getTime() < startDate.getTime()) {
                        return 'End date must be on or after start date';
                      }
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <DatePicker
                      label="End date"
                      value={field.value}
                      onChange={field.onChange}
                      disabled={disabled || isSubmitting}
                      format="dd/MM/yyyy"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: Boolean(errors.endDate),
                          helperText: errors.endDate?.message,
                        },
                      }}
                    />
                  )}
                />
              </Stack>

              {/* Duration Calculator & Allocation Summary */}
              {duration && (
                <Fade in={true}>
                  <Paper
                    elevation={0}
                    sx={{
                      mt: 2.5,
                      p: 2.5,
                      backgroundColor: 'primary.50',
                      border: '1px solid',
                      borderColor: 'primary.200',
                      borderRadius: 2,
                    }}
                  >
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUpIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2" fontWeight={600} color="primary.main">
                          Leave Summary
                        </Typography>
                      </Box>

                      {/* This Request */}
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, fontWeight: 600 }}>
                          This Request
                        </Typography>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
                            gap: 1.5,
                          }}
                        >
                          <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                            <Typography variant="h5" fontWeight={700} color="primary.main">
                              {duration.total}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Total Days
                            </Typography>
                          </Box>

                          <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                            <Typography variant="h5" fontWeight={700}>
                              {duration.business}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Weekdays
                            </Typography>
                          </Box>

                          {duration.weekends > 0 && (
                            <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                              <Typography variant="h5" fontWeight={700} color="text.secondary">
                                {duration.weekends}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Weekend Days
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>

                      {/* Allocation Overview */}
                      {allocationSummary && (
                        <>
                          <Divider />
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, fontWeight: 600 }}>
                              Your Leave Balance
                            </Typography>
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
                                gap: 1.5,
                              }}
                            >
                              <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                                <Typography variant="h5" fontWeight={700} color="success.main">
                                  {allocationSummary.approvedDays}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Approved
                                </Typography>
                              </Box>

                              <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                                <Typography variant="h5" fontWeight={700} color="warning.main">
                                  {allocationSummary.pendingDays}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Pending
                                </Typography>
                              </Box>

                              {allocationSummary.allowanceDays && (
                                <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                                  <Typography variant="h5" fontWeight={700} color="info.main">
                                    {Math.max(0, allocationSummary.allowanceDays - allocationSummary.approvedDays - allocationSummary.pendingDays - duration.total)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Remaining
                                  </Typography>
                                </Box>
                              )}
                            </Box>

                            {allocationSummary.allowanceDays && (
                              <Box sx={{ mt: 1.5, textAlign: 'center' }}>
                                <Typography variant="caption" color="text.secondary">
                                  Annual Allowance: <strong>{allocationSummary.allowanceDays} days</strong>
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </>
                      )}

                      <Divider />

                      {/* Date Range */}
                      <Stack spacing={0.5}>
                        <Typography variant="caption" color="text.secondary">
                          📅 {duration.startFormatted}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          📅 {duration.endFormatted}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Paper>
                </Fade>
              )}
            </CardContent>
          </Card>

          {/* Reason & Comments Card */}
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                <DescriptionOutlinedIcon color="action" />
                <Typography variant="h6" fontWeight={600}>
                  Request Details
                </Typography>
              </Box>

              <Stack spacing={2.5}>
                <Controller
                  name="reason"
                  control={control}
                  rules={{ required: 'Please provide a short reason for leave' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Reason for leave"
                      placeholder="e.g. Annual leave for family vacation, medical appointment, etc."
                      minRows={3}
                      multiline
                      fullWidth
                      disabled={disabled || isSubmitting}
                      error={Boolean(errors.reason)}
                      helperText={errors.reason?.message || 'Be clear and concise to help managers approve quickly'}
                    />
                  )}
                />

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <CommentOutlinedIcon fontSize="small" color="action" />
                    <Typography variant="body2" fontWeight={600} color="text.secondary">
                      Additional Comments (Optional)
                    </Typography>
                  </Box>
                  <Controller
                    name="comment"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        placeholder="Any additional context or notes for your manager..."
                        minRows={2}
                        multiline
                        fullWidth
                        disabled={disabled || isSubmitting}
                      />
                    )}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ pt: 1 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disabled={disabled || isSubmitting}
              sx={{
                py: 1.5,
                fontWeight: 600,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4,
                },
              }}
            >
              {submitLabel}
            </Button>
            <Button
              type="button"
              variant="outlined"
              size="large"
              disabled={isSubmitting}
              onClick={() => reset(defaultValues)}
              sx={{ py: 1.5 }}
            >
              Clear Form
            </Button>
          </Stack>
        </Stack>
      </Box>
    </LocalizationProvider>
  );
};

export default LeaveRequestForm;
